import React from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom-v5-compat";
import { twJoin } from "tailwind-merge";
import { z } from "zod/v4";

import { Label } from "@onsite_app/MobileOrderingMenu/Atoms/Form";
import NavBarTitle from "@onsite_app/MobileOrderingMenu/Atoms/Navbar";
import { useVenue } from "@onsite_app/MobileOrderingMenu/VenueContext";

import { VoucherCardPreview } from "./VoucherCartItems/VoucherCardPreview";
import { VoucherCartQueries } from "./queries";
import { Voucher } from "./schema";

const ParamsSchema = z.object({
  voucherCheckoutUuid: z.string(),
});
const EmailBlock = ({
  message,
  email,
}: {
  message: string;
  email: string | null;
}) => (
  <p className="leading-snug">
    {message}
    <br />
    <span className="font-semibold">{` (${email})`}</span>
  </p>
);
const VoucherSentInformation = ({ voucher }: { voucher: Voucher }) => {
  const { t } = useTranslation("translations", {
    keyPrefix: "voucherCheckoutSuccess",
  });
  switch (voucher.creationReason) {
    case "gift_for_someone_else":
      return (
        <>
          <EmailBlock
            message={t("giftVoucherSentToRecipient")}
            email={voucher.recipientEmail}
          />
          <EmailBlock
            message={t("voucherReceiptSentToYou")}
            email={voucher.purchaserEmail}
          />
        </>
      );
    case "gift_for_unknown_recipient":
      return (
        <EmailBlock
          message={t("unknownRecipientVoucherHasBeenSentToYou")}
          email={voucher.purchaserEmail}
        />
      );

    case "self_purchase":
      return (
        <EmailBlock
          message={t("selfPurchaseVoucherSentToYou")}
          email={voucher.purchaserEmail}
        />
      );
    default:
      return null;
  }
};

const VoucherCheckoutSuccess = () => {
  const { t } = useTranslation("translations", {
    keyPrefix: "voucherCheckoutSuccess",
  });

  const venueName = useVenue().name;
  const checkoutUuid = ParamsSchema.parse(useParams()).voucherCheckoutUuid;

  const checkoutSession = useSuspenseQuery(
    VoucherCartQueries.checkoutSession(checkoutUuid),
  ).data;

  const checkoutSessionVouchers = useSuspenseQuery(
    VoucherCartQueries.checkoutSessionVouchers(checkoutSession.originatorId),
  ).data;

  return (
    <div className="mx-auto space-y-32 py-8 text-center">
      <NavBarTitle title={t("title")} />

      <div>
        <p>{t("yourOrderHasBeenSuccessfullyPlaced")}</p>
      </div>

      <div className="space-y-16">
        <Label>
          {t("yourPurchasedVouchers", {
            count: checkoutSessionVouchers.length,
          })}
        </Label>
        <div
          className={twJoin(
            "grid place-items-center justify-center gap-y-32",
            checkoutSessionVouchers.length === 1
              ? "lg:grid-cols-1"
              : "lg:grid-cols-2",
          )}
        >
          {checkoutSessionVouchers.map((voucher) => (
            <div key={voucher.uuid} className="space-y-16">
              <div className="">
                <div>
                  <VoucherCardPreview voucher={voucher} />
                </div>
                <div className="space-y-8 sm:py-16">
                  <VoucherSentInformation
                    voucher={{
                      ...voucher,
                    }}
                  />
                </div>
              </div>
              <div className="custom-dashed mx-auto w-1/3" />
            </div>
          ))}
        </div>
      </div>
      <div>
        <p>{t("thankYouForYourPurchase")}</p>
        <p>{t("messageFromTheVenue", { venueName })}</p>
      </div>
    </div>
  );
};

export default VoucherCheckoutSuccess;
